/**
 * crearContinuacionLocal.ts — child report creation (D3, REQ-5).
 *
 * A child report is just a NEW report that inherits data from a parent. It follows
 * the SAME lifecycle as NuevoInforme:
 *   1. Read the parent payload from IndexedDB and build the child payload.
 *   2. Write the child optimistically to IDB as a BORRADOR (works offline).
 *   3. ONLINE: POST to the server (with parentId + the inherited payload) so the
 *      server mints the número and returns a serverId — exactly like NuevoInforme.
 *      The local record is then reconciled with serverId + número.
 *   4. OFFLINE / POST failure: the child stays as a local borrador and syncs later
 *      via the normal borrador→finalizar flow.
 *
 * The server re-merges the parent payload (CreateInformeUseCase), but we send the
 * COMPLETE child payload as input, so our fields — including the intentionally
 * emptied ones (e.g. AC conclusiones) — win over the server's re-inheritance.
 *
 * // ponytail: mirrors NuevoInforme's putReport → createInforme → putReport flow.
 */

import { getReport, getReportByServerId, putReport } from "@/features/offline-sync/index.js";
import { buildChildPayload } from "./build-child-payload.js";
import { createInforme, getInforme } from "../services/reports.service.js";
import type { IePayload, IsPayload, NuevoInformeInput, TipoInforme } from "../types.js";

type ChildTipo = Extract<TipoInforme, "IS" | "AC">;
type ParentTipo = Extract<TipoInforme, "IE" | "IS">;

/**
 * Creates a child report locally from a parent in IndexedDB.
 *
 * Tries lookup by localId first, then by serverId (for online reports whose
 * IDB record was written with a server-assigned ID as localId).
 *
 * @param parentId  - The localId or serverId of the parent report in IndexedDB.
 * @param childTipo - "IS" or "AC".
 * @param clienteId - Optional clienteId (passed through to the child record).
 * @param cliente   - Optional free-text client name.
 * @returns         - The serverId when created online, otherwise the local childId.
 * @throws          - If the parent is not found in IndexedDB (S-5.3).
 */
export async function crearContinuacionLocal(
  parentId: string,
  childTipo: ChildTipo,
  clienteId?: string,
  cliente?: string,
): Promise<string> {
  // Step 1: resolve the parent. Prefer IDB (offline / technician flow); if it is not
  // there (e.g. an admin viewing a report loaded from the network, or a report created
  // on another device), fetch it from the server. Only when BOTH fail — offline and no
  // local copy — do we surface the unavailable error.
  const local =
    (await getReport(parentId)) ??
    (await getReportByServerId(parentId));

  let parentTipo: ParentTipo;
  let parentPayload: IePayload | IsPayload;
  let parentServerId: string | undefined;
  let parentLocalId: string;
  let parentCliente: string | undefined;

  if (local) {
    parentTipo = local.tipo as ParentTipo;
    parentPayload = local.payload as IePayload | IsPayload;
    parentServerId = local.serverId;
    parentLocalId = local.localId;
    parentCliente = local.cliente;
  } else {
    let remote;
    try {
      remote = await getInforme(parentId);
    } catch {
      // S-5.3: not in IDB and not reachable → surface a user-facing error
      throw new Error("El informe padre no está disponible sin conexión.");
    }
    parentTipo = remote.tipo as ParentTipo;
    parentPayload = remote.payload as unknown as IePayload | IsPayload;
    parentServerId = remote.id;
    parentLocalId = remote.id;
    parentCliente = remote.cliente;
  }

  const clienteFinal = cliente ?? parentCliente;

  // Step 2: build child payload from the parent payload
  const childPayload = buildChildPayload(parentPayload, parentTipo, childTipo);

  // Step 3: mint a fresh localId for the child
  const childLocalId = crypto.randomUUID();
  const now = new Date().toISOString();

  // Step 4: optimistic local write as BORRADOR (works offline, matches NuevoInforme).
  await putReport({
    localId: childLocalId,
    tipo: childTipo,
    clienteId,
    cliente: clienteFinal,
    parentLocalId,
    estado: "borrador",
    payload: childPayload as unknown as Record<string, unknown>,
    updatedAt: now,
  });

  // Step 5: ONLINE — POST immediately so the server mints the número + serverId,
  // exactly like NuevoInforme. On any failure (offline / network), keep the local
  // borrador and let the normal finalizar→sync flow handle it later.
  try {
    const input: NuevoInformeInput = {
      tipo: childTipo,
      parentId: parentServerId ?? null,
      payload: childPayload as unknown as Record<string, unknown>,
      ...(clienteId !== undefined ? { clienteId } : { cliente: clienteFinal }),
    };
    const nuevo = await createInforme(input);
    // Reconcile the local record with the server id + número.
    await putReport({
      localId: childLocalId,
      serverId: nuevo.id,
      numero: nuevo.numero,
      tipo: childTipo,
      clienteId: nuevo.clienteId ?? undefined,
      cliente: nuevo.cliente,
      parentLocalId,
      estado: nuevo.estado,
      payload: nuevo.payload,
      updatedAt: nuevo.updatedAt,
    });
    return nuevo.id;
  } catch {
    // Offline or POST failed → stays as a local borrador, syncs later.
    return childLocalId;
  }
}
